/**
 * Global incoming 1:1 call dialog (web only).
 *
 * Driven by the presence context's `incomingCall`. Opens whenever a `ring`
 * arrives over the presence socket and offers Accept (join the pair room) or
 * Decline (dismiss + tell the caller). Auto-closes when the ring is cleared
 * (declined elsewhere, cancelled by the caller, or the 30s timeout in the
 * provider).
 */

import {useEffect} from 'react'
import {View} from 'react-native'
import {msg} from '@lingui/core/macro'
import {useLingui} from '@lingui/react'
import {Trans} from '@lingui/react/macro'

import {logger} from '#/logger'
import {useModerationOpts} from '#/state/preferences/moderation-opts'
import {useIncomingCall} from '#/state/presence'
import {declineRing} from '#/state/presence/ring'
import {useProfilesQuery} from '#/state/queries/profile'
import {useAgent, useSession} from '#/state/session'
import {atoms as a, useTheme} from '#/alf'
import {Button, ButtonText} from '#/components/Button'
import * as Dialog from '#/components/Dialog'
import * as ProfileCard from '#/components/ProfileCard'
import {Text} from '#/components/Typography'
import {navigate} from '#/Navigation'

export function IncomingCallDialog() {
  const control = Dialog.useDialogControl()
  const {incomingCall, clearIncomingCall} = useIncomingCall()

  // Open when a ring arrives; close when it's cleared. `control` is stable for
  // the dialog's lifetime, so keying on presence of a ring is enough.
  const hasIncomingCall = !!incomingCall
  useEffect(() => {
    if (hasIncomingCall) {
      control.open()
    } else {
      control.close()
    }
  }, [hasIncomingCall, control])

  return (
    <Dialog.Outer
      control={control}
      onClose={() => {
        // If dismissed by tapping the backdrop (not via a button), drop the ring.
        if (incomingCall) clearIncomingCall()
      }}>
      <Dialog.Handle />
      {incomingCall && (
        <IncomingCallDialogInner
          control={control}
          fromDid={incomingCall.fromDid}
          fromHandle={incomingCall.fromHandle}
          roomSlug={incomingCall.roomSlug}
          clearIncomingCall={clearIncomingCall}
        />
      )}
    </Dialog.Outer>
  )
}

function IncomingCallDialogInner({
  control,
  fromDid,
  fromHandle,
  roomSlug,
  clearIncomingCall,
}: {
  control: Dialog.DialogControlProps
  fromDid: string
  fromHandle?: string
  roomSlug: string
  clearIncomingCall: () => void
}) {
  const {_} = useLingui()
  const t = useTheme()
  const agent = useAgent()
  const {currentAccount} = useSession()
  const moderationOpts = useModerationOpts()
  const {data} = useProfilesQuery({handles: [fromDid]})
  const profile = data?.profiles?.[0]

  const displayName =
    profile?.displayName || profile?.handle || fromHandle || _(msg`Someone`)

  const onAccept = () => {
    control.close(() => {
      clearIncomingCall()
      void navigate('Call', {roomId: roomSlug})
    })
  }

  const onDecline = () => {
    control.close(() => {
      clearIncomingCall()
      declineRing(agent, fromDid, currentAccount?.handle).catch(err =>
        logger.warn('declineRing failed', {safeMessage: err}),
      )
    })
  }

  return (
    <Dialog.ScrollableInner
      label={_(msg`Incoming call`)}
      style={[a.w_full, {maxWidth: 380}]}>
      <View style={[a.align_center, a.gap_md, a.py_md]}>
        <Text style={[a.text_sm, t.atoms.text_contrast_medium]}>
          <Trans>Incoming call</Trans>
        </Text>

        {profile && moderationOpts ? (
          <View style={[a.align_center, a.gap_sm]}>
            <ProfileCard.Avatar
              profile={profile}
              moderationOpts={moderationOpts}
              size={72}
              disabledPreview
            />
            <Text style={[a.text_lg, a.font_bold, t.atoms.text]} emoji>
              {displayName}
            </Text>
          </View>
        ) : (
          <Text style={[a.text_lg, a.font_bold, t.atoms.text]} emoji>
            {displayName}
          </Text>
        )}

        <Text style={[a.text_sm, t.atoms.text_contrast_medium]}>
          <Trans>wants to start a video call</Trans>
        </Text>

        <View style={[a.flex_row, a.gap_md, a.mt_md, a.w_full]}>
          <Button
            label={_(msg`Decline`)}
            onPress={onDecline}
            color="secondary"
            size="large"
            style={[a.flex_1]}>
            <ButtonText>
              <Trans>Decline</Trans>
            </ButtonText>
          </Button>
          <Button
            label={_(msg`Accept`)}
            onPress={onAccept}
            color="primary"
            size="large"
            style={[a.flex_1]}>
            <ButtonText>
              <Trans>Accept</Trans>
            </ButtonText>
          </Button>
        </View>
      </View>
    </Dialog.ScrollableInner>
  )
}
